<!-- src/pages/auth/login.vue -->
<template>
  <form @submit.prevent="handleSubmit">
    <section class="page-login">
      <div class="container d-flex justify-content-center align-items-center min-vh-100">
        <div class="card p-4">
          <!-- Logo -->
          <div class="text-center mb-4">
            <div class="app-logo mb-2" aria-hidden="true">
              <i class="fas fa-bolt text-primary fs-1"></i>
            </div>
            <h1 class="h4 fw-bold mb-1">NeXt Instalações</h1>
            <p class="text-muted mb-0">Acesse sua conta para continuar</p>
          </div>

          <!-- Username -->
          <div class="mb-3 position-relative">
            <label for="username" class="form-label">{{ $t('page.auth.register.inputs.username') }}</label>
            <div class="position-relative">
              <i class="fas fa-user input-icon" aria-hidden="true"></i>
              <input id="username" type="text" class="form-control ps-5" :class="{ 'is-invalid': errors.username }" v-model.trim="formData.username" :placeholder="$t('page.auth.register.inputs.usernamePlaceholder')" required autocomplete="username" />
            </div>
            <small v-if="errors.username" class="text-danger">{{ errors.username[0] }}</small>
          </div>

          <!-- Password -->
          <div class="mb-3 position-relative">
            <label for="password" class="form-label">{{ $t('page.auth.register.inputs.password') }}</label>
            <div class="position-relative">
              <i class="fas fa-lock input-icon" aria-hidden="true"></i>
              <input id="password" :type="showPassword ? 'text' : 'password'" class="form-control ps-5 pe-5" v-model="formData.password" :placeholder="$t('page.auth.register.inputs.passwordPlaceholder')" required autocomplete="current-password" aria-describedby="togglePassword" />

              <button id="togglePassword" type="button" class="btn btn-link password-toggle" :aria-pressed="String(showPassword)" :aria-label="showPassword ? $t('page.auth.register.hide_password') : $t('page.auth.register.show_password')"  @click="togglePassword">
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <small v-if="errors.password" class="text-danger">{{ errors.password[0] }}</small>
          </div>

          <!-- Checkbox -->
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="remember" v-model="remember" />
              <label class="form-check-label small" for="remember">
                {{ $t('page.auth.login.remember_me') }}
              </label>
            </div>

            <router-link :to="{ name: 'forgot-password' }" class="fw-semibold text-decoration-none">
              {{ $t('page.auth.login.forgot_password') }}
            </router-link>
          </div>

          <!-- Botão -->
          <div class="d-grid mb-3">
            <button class="btn btn-primary" type="submit" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ loading ? $t('page.auth.login.signing_in') : $t('page.auth.login.sign_in') }}
            </button>
          </div>

          <!-- Links -->
          <div class="text-center mb-3">
            <p class="text-muted small mb-2">{{ $t('page.auth.login.or_continue_with') }}</p>
            <div class="d-flex justify-content-center gap-3">
              <button type="button" class="btn btn-light border" @click="loginWith('google')" aria-label="Login com Google">
                <i class="fab fa-google text-secondary"></i>
              </button>
              <button type="button" class="btn btn-light border" @click="loginWith('facebook')" aria-label="Login com Facebook">
                <i class="fab fa-facebook text-secondary"></i>
              </button>
              <button type="button" class="btn btn-light border" @click="loginWith('github')" aria-label="Login com GitHub">
                <i class="fab fa-github text-secondary"></i>
              </button>
            </div>
          </div>

          <!-- SingUp -->
          <div class="text-center">
            <p class="small text-muted">
              {{ $t('page.auth.login.no_account') }}
              <router-link :to="{ name: 'register' }" class="fw-semibold text-decoration-none">
                {{ $t('page.auth.login.create_account') }}
              </router-link>
            </p>
          </div>

          <!-- Footer -->
          <div class="text-center mt-3">
            <small class="text-muted">© 2019 - {{ new Date().getFullYear() }} Next Instalações</small>
          </div>
        </div>
      </div>
    </section>
  </form>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/authStore";

// Consts
const router = useRouter();
const authStore = useAuthStore();

// States
const formData = ref({
  username: "",
  password: "",
});
const showPassword = ref(false);
const remember = ref(false);
const loading = ref(false);
const errors = ref({});

const togglePassword = () => (showPassword.value = !showPassword.value);

// Submit
const handleSubmit = async () => {
  loading.value = true;
  errors.value = {}; 
  try {
    await authStore.login(formData.value);
    router.push({ name: "home" }).catch(() => { });
  } catch (error) {
    if (error.response?.data?.errors) {
      errors.value = error.response.data.errors;
    }
  } finally {
    loading.value = false;
  }
};

// Auth Social
const loginWith = (provider) => {
  alert(`${provider} in development`);
};

// Mounted
onMounted(() => {
  if (authStore.isAuthenticated) {
    router.push({ name: "home" }).catch(() => { });
  }
});
</script>

<style scoped>
.page-login {
  background: linear-gradient(135deg, #2c3e50, #4a6580);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.card {
  width: 100%;
  max-width: 420px;
  border-radius: 1rem;
  background: #fff;
  box-shadow: 0 .5rem 1rem rgba(0, 0, 0, .15) !important;
}

.app-logo {
  width: 70px;
  height: 70px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.input-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 0.95rem;
  pointer-events: none;
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: pointer;
}

.password-toggle:focus {
  outline: none;
  box-shadow: none;
}

.form-control.ps-5 {
  padding-left: 2.5rem !important;
}

.form-control.pe-5 {
  padding-right: 2.5rem !important;
}

.form-control {
  height: 44px;
  border-radius: 8px;
}

.btn {
  height: 44px;
  border-radius: 8px;
}

@media (max-width: 576px) {
  .page-login {
    background: none;
  }

  .card {
    border: none;
    border-radius: none;
    box-shadow: none !important;
  }
}
</style>
